import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useLang } from "@/LangContext";
import { Reveal, Chapter } from "@/components/Reveal";
import es from "@/locales/es";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  representative_name: "",
  company_name: "",
  corporate_email: "",
  phone_whatsapp: "",
  interest_index: "",
  message: "",
};

const inputCls =
  "w-full border border-white/10 bg-[#0A0C10]/70 px-4 py-3.5 text-sm text-white placeholder:text-slate-600 transition-colors focus:border-amber-400/60 focus:outline-none";
const labelCls =
  "mb-2 block font-code text-[10px] uppercase tracking-[0.28em] text-slate-400";

export default function ContactForm() {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) =>
      setForm((f) => ({ ...f, interest_index: String(e.detail) }));
    window.addEventListener("montalban:set-interest", handler);
    return () => window.removeEventListener("montalban:set-interest", handler);
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        representative_name: form.representative_name.trim(),
        company_name: form.company_name.trim(),
        corporate_email: form.corporate_email.trim(),
        phone_whatsapp: form.phone_whatsapp.trim(),
        interest_area: es.contact.interests[Number(form.interest_index)],
        message: form.message.trim() || null,
      });
      toast.success(t.contact.success);
      setForm(initialForm);
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative border-t border-white/5 bg-[#0C0F16] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal>
              <Chapter number={t.contact.chapter} label={t.contact.label} />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
                {t.contact.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                {t.contact.subtitle}
              </p>
              <p
                data-testid="contact-privacy-note"
                className="mt-8 border-l-2 border-amber-400 pl-5 text-sm leading-relaxed text-slate-400"
              >
                {t.contact.privacy}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="border border-white/10 bg-[#121620]/80 p-7 backdrop-blur-sm sm:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="form-name">
                    {t.contact.name} *
                  </label>
                  <input
                    id="form-name"
                    data-testid="form-name-input"
                    required
                    value={form.representative_name}
                    onChange={set("representative_name")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="form-company">
                    {t.contact.company} *
                  </label>
                  <input
                    id="form-company"
                    data-testid="form-company-input"
                    required
                    value={form.company_name}
                    onChange={set("company_name")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="form-email">
                    {t.contact.email} *
                  </label>
                  <input
                    id="form-email"
                    data-testid="form-email-input"
                    type="email"
                    required
                    value={form.corporate_email}
                    onChange={set("corporate_email")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="form-phone">
                    {t.contact.phone} *
                  </label>
                  <input
                    id="form-phone"
                    data-testid="form-phone-input"
                    type="tel"
                    required
                    value={form.phone_whatsapp}
                    onChange={set("phone_whatsapp")}
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="form-interest">
                    {t.contact.interest} *
                  </label>
                  <select
                    id="form-interest"
                    data-testid="form-interest-select"
                    required
                    value={form.interest_index}
                    onChange={set("interest_index")}
                    className={`${inputCls} appearance-none ${
                      form.interest_index === "" ? "text-slate-600" : ""
                    }`}
                  >
                    <option value="" disabled>
                      {t.contact.interestPlaceholder}
                    </option>
                    {t.contact.interests.map((opt, i) => (
                      <option
                        key={opt}
                        value={i}
                        className="bg-[#121620] text-white"
                      >
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="form-message">
                    {t.contact.message}
                  </label>
                  <textarea
                    id="form-message"
                    data-testid="form-message-input"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <button
                data-testid="form-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-amber-400 px-6 py-4 font-display text-base font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {t.contact.submitting}
                  </>
                ) : (
                  <>
                    {t.contact.submit}
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1.5"
                    />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
